import { createAsyncThunk } from "@reduxjs/toolkit";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { API_URL } from "config";
import { configState } from "state/config-slice";
import { MedalliaHistoryEntry } from "types/MedalliaData";

export const getSurveyTypeFromFormId = (
  formId: string | undefined,
  forms: Array<{ id: string; type: string }> = [],
): string => {
  const matchingForm = forms.find((form) => form.id === formId);

  return matchingForm?.type ?? "activation";
};

export const getMedalliaEventKey = (data: {
  clientEventUuid?: string;
  feedbackCorrelationUuid?: string;
  feedbackUuid?: string;
  formId?: string;
  userId?: string;
}): string => {
  return `${data.feedbackCorrelationUuid ?? data.feedbackUuid ?? data.clientEventUuid ?? ""}:${data.formId ?? ""}:${
    data.userId ?? ""
  }`;
};

export const shouldSubmitMedalliaEvent = (
  eventName: string | undefined,
  eventKey: string,
  submittedEventKeys: Set<string>,
): boolean => {
  if (eventName === "nebula_form_submitted") return true;
  if (eventName !== "nebula_form_closed") return false;

  return !submittedEventKeys.has(eventKey);
};

export const buildSurveyHistoryPayload = (
  history: MedalliaHistoryEntry[] | undefined,
  newEntry: MedalliaHistoryEntry,
): MedalliaHistoryEntry[] => {
  return [...(Array.isArray(history) ? history : []), newEntry];
};

export const submitSurveyData = createAsyncThunk(
  "survey/submitSurveyData",
  async (payload: unknown, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/api/v1/survey`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      return response.data;
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { data?: unknown } }).response?.data !== "undefined"
      ) {
        return rejectWithValue((error as { response: { data: unknown } }).response.data);
      }

      return rejectWithValue((error as Error).message);
    }
  },
);

const useMedalliaInterceptor = () => {
  const dispatch = useDispatch();
  const { survey } = useSelector(configState);
  const submittedEventKeysRef = useRef(new Set<string>());

  useEffect(() => {
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
      this.addEventListener("load", function () {
        if (this.responseURL.includes("https://analytics-fe.digital-cloud.medallia.eu/api/web/events")) {
          try {
            if (typeof body === "string") {
              const payload = JSON.parse(body);
              console.log("Medallia payload intercepted:", payload);
              const data = payload[0];
              const eventKey = getMedalliaEventKey(data);

              const action = data.eventName === "nebula_form_closed" ? "skipped" : "completed";

              console.log("data.eventName----", data.eventName);
              console.log("data.userid----", data.userId);

              const newHistoryEntry: MedalliaHistoryEntry = {
                date: new Date().toISOString(),
                surveyType: getSurveyTypeFromFormId(data.formId, survey?.config?.forms),
                action,
                medallia: {
                  feedbackCorrelationUuid: data.clientEventUuid,
                  feedbackUuid: data.clientEventUuid,
                  formId: data.formId,
                  userId: data.userId,
                  timestamp: data.timestamp,
                  sessionPlatform: data.sessionPlatform,
                  pageUrl: data.pageUrl,
                },
              };

              const sendPayload = buildSurveyHistoryPayload(survey?.history, newHistoryEntry);

              if (data.eventName === "nebula_form_submitted") {
                submittedEventKeysRef.current.add(eventKey);
              }

              if (shouldSubmitMedalliaEvent(data.eventName, eventKey, submittedEventKeysRef.current)) {
                dispatch(submitSurveyData(sendPayload));
              }
            } else {
              console.warn("Medallia payload is not a string:", body);
            }
          } catch (err) {
            console.error("Failed to parse Medallia payload", err);
          }
        }
      });

      originalSend.call(this, body);
    };

    return () => {
      XMLHttpRequest.prototype.send = originalSend;
    };
  }, [dispatch, survey?.config?.forms, survey?.history]);
};

export default useMedalliaInterceptor;
